import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Award,
  Plus,
  Edit,
  Trash2,
  Eye,
  Calendar,
  Star,
  FileText,
  Upload,
  X,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import api from '../utils/axios';
import toast from 'react-hot-toast';
import Pagination from '../components/Pagination';
import CFiltersBar from '../components/shared/c-filters-bar';
import CTable from '../components/shared/c-table';
import { APPRAISAL_STATUSES } from '../constants';
import { buildQueryParams } from '../utils/query';
import { formatDateOnly, getUniqueYears } from '../utils/dateUtils';
import { formatRaise } from '../utils/currency';
import { getStatusBadgeClasses, formatStatusText } from '../utils/statusUtils';
import { useAuth } from '../contexts/AuthContext';
import SummaryCard from '../components/shared/summary-card';
import CModal from '../components/shared/c-modal';
import usePersistedState from '../hooks/usePersistedState';
import useLatestRequest from '../hooks/useLatestRequest';
import { canEditAppraisal } from '../utils/compensationPermissions';

// Rendered twice: in the page header and in the empty state. They need distinct
// test ids, so the id is a prop -- otherwise an empty list renders
// appraisals-add-btn twice and a strict getByTestId matches two elements.
const AddAppraisalButton = ({ testId = 'appraisals-add-btn' }) => (
  <Link
    to="/appraisals/add"
    data-testid={testId}
    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
  >
    <Plus className="h-4 w-4 mr-2" />
    Add Appraisal
  </Link>
);

const Appraisals = () => {
  const { hasPermission, user } = useAuth();
  // Same rule as EditAppraisal and the server: update_own (every employee's self-review
  // grant) never surfaces edit or activate/deactivate, and a manager's update_reports
  // does not reach their own appraisal.
  const canEditRow = (appraisal) => canEditAppraisal(hasPermission, user, appraisal);
  const [appraisals, setAppraisals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTerm, setSearchTerm] = usePersistedState('appraisals.search', '');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const [statusFilter, setStatusFilter] = usePersistedState(
    'appraisals.status',
    ''
  );
  const [yearFilter, setYearFilter] = usePersistedState('appraisals.year', '');
  const [departmentFilter, setDepartmentFilter] = usePersistedState(
    'appraisals.department',
    ''
  );
  const [departments, setDepartments] = useState([]);
  const [page, setPage] = usePersistedState('appraisals.page', 1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteAppraisalId, setDeleteAppraisalId] = useState(null);
  const [appraisalStats, setAppraisalStats] = useState({
    total: 0,
    completed: 0,
    pending: 0,
    overdue: 0,
    avg_rating: 0,
    this_year: 0,
  });
  // null = not known (loading or the fetch failed); rendered as '-' rather than a
  // 0 that would read as "nothing due".
  const [upcomingCount, setUpcomingCount] = useState(null);
  const [showUploadResults, setShowUploadResults] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Debounce search term to prevent excessive API calls. A new search starts
  // from page 1 (see changeFilter). Skipped while the two agree, so mounting
  // with a remembered search does not throw away the remembered page.
  useEffect(() => {
    if (searchTerm === debouncedSearchTerm) return undefined;
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(1);
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearchTerm, setPage]);

  // Narrowing the list from page 3 would otherwise request page 3 of a result
  // that may only have one page.
  const changeFilter = (setter) => (e) => {
    setter(e.target.value);
    setPage(1);
  };

  // Only the latest load may fill the list or move the page: a page click or a filter
  // change while one is in flight starts another. Each response used to write its page
  // back, which let two loads in flight flip the page between them, refetching on
  // every flip. The page asked for is the page served, so only the redirect below
  // still moves it.
  const beginRequest = useLatestRequest();

  useEffect(() => {
    fetchAppraisals();
    // eslint-disable-next-line
  }, [
    page,
    limit,
    debouncedSearchTerm,
    statusFilter,
    yearFilter,
    departmentFilter,
  ]);

  const fetchAppraisals = async () => {
    const isCurrent = beginRequest();
    let redirected = false;
    try {
      setSearchLoading(true);
      const params = buildQueryParams({
        page,
        limit,
        search: debouncedSearchTerm,
        status: statusFilter,
        year: yearFilter,
        department_id: departmentFilter,
      });
      const response = await api.get('/api/appraisals', { params });
      if (!isCurrent()) return;
      const { total: rowTotal, page: servedPage, limit: servedLimit } = response.data;
      const rows = Array.isArray(response.data.data) ? response.data.data : [];
      setTotal(rowTotal);
      // The requested page no longer exists: its last row was deleted, or it is a
      // remembered page (sessionStorage) past the end of today's results. Echoing
      // it back would show "No appraisals found" with no pagination while rows
      // exist, so go to the last page that does; the page effect refetches. Only
      // ever moves backwards, so an odd response cannot loop or strand the spinner.
      const lastPage = Math.ceil(rowTotal / servedLimit);
      if (rows.length === 0 && rowTotal > 0 && servedPage > lastPage) {
        redirected = true;
        setPage(lastPage);
        return;
      }
      setAppraisals(rows);
    } catch (error) {
      if (!isCurrent()) return;
      console.error('Error fetching appraisals:', error);
      toast.error('Failed to load appraisals');
    } finally {
      // Stay in the loading state across a redirect. On first load that keeps the
      // page spinner up; after a delete it keeps the table's spinner up (CTable
      // gets searchLoading), so neither the empty page nor the stale, deleted row
      // with live actions is shown until the corrected fetch lands.
      if (!redirected && isCurrent()) {
        setLoading(false);
        setSearchLoading(false);
      }
    }
  };

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const res = await api.get('/api/appraisals/departments');
        setDepartments(res.data.data || []);
      } catch (err) {
        setDepartments([]);
      }
    };
    fetchDepartments();
  }, []);

  // Same query as the Dashboard's Upcoming Appraisals panel: active appraisals
  // whose next review falls in the next 30 days, across every page. Counting the
  // current page's rows by appraisal_date disagreed with both the Dashboard and
  // this table's Next Review column.
  const fetchUpcomingCount = async () => {
    try {
      const response = await api.get('/api/appraisals/upcoming/list', {
        params: { days: 30 },
      });
      setUpcomingCount(response.data.data?.length || 0);
    } catch (error) {
      console.error('Error fetching upcoming appraisals:', error);
      setUpcomingCount(null);
    }
  };

  const fetchAppraisalStats = async () => {
    try {
      const response = await api.get('/api/appraisals/statistics/overview');
      setAppraisalStats({
        total: response.data.data.total_appraisals?.count || 0,
        completed: response.data.data.completed_appraisals?.count || 0,
        pending: response.data.data.pending_appraisals?.count || 0,
        overdue: response.data.data.overdue_appraisals?.count || 0,
        avg_rating: response.data.data.avg_rating?.average
          ? Number(response.data.data.avg_rating.average)
          : 0,
        this_year: response.data.data.this_year?.count || 0,
      });
    } catch (error) {
      console.error('Error fetching appraisal statistics:', error);
      // Don't show toast for stats failure, just log it
    }
  };

  // The stat cards ignore the table's filters and paging, so they load once and
  // again only after something changes the data (toggle, delete, bulk upload).
  // Creating happens on /appraisals/add, which remounts this page on return.
  const refreshStatCards = () => {
    fetchAppraisalStats();
    fetchUpcomingCount();
  };

  useEffect(() => {
    refreshStatCards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalPages = Math.ceil(total / limit);

  const handleDelete = (id) => {
    setDeleteAppraisalId(id);
    setShowDeleteModal(true);
  };

  const toggleAppraisalActive = async (id, currentStatus) => {
    try {
      await api.patch(`/api/appraisals/${id}/toggle-active`);
      toast.success(
        `Appraisal ${!currentStatus ? 'activated' : 'deactivated'} successfully`,
      );
      fetchAppraisals();
      refreshStatCards();
    } catch (error) {
      toast.error(
        error.response?.data?.message || 'Failed to toggle appraisal status',
      );
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/api/appraisals/${deleteAppraisalId}`);
      toast.success('Appraisal deleted successfully');
      // Refetch rather than filter locally, so the total, the page and the stat
      // cards all reflect the deletion.
      fetchAppraisals();
      refreshStatCards();
    } catch (err) {
      toast.error('Failed to delete appraisal');
    }
    setShowDeleteModal(false);
    setDeleteAppraisalId(null);
  };

  const getYears = () => {
    if (!appraisals || !Array.isArray(appraisals)) return [];
    const years = getUniqueYears(appraisals);
    return Array.isArray(years) ? years : [];
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/api/appraisals/bulk-template', {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'appraisal-upload-template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Failed to download template');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/api/appraisals/bulk-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadResults(response.data);
      setShowUploadResults(true);

      if (response.data.summary.success > 0) {
        toast.success(
          `Successfully uploaded ${response.data.summary.success} appraisals`,
        );
        fetchAppraisals(); // Refresh the list
        refreshStatCards();
      }

      if (response.data.summary.failed > 0) {
        toast.error(
          `${response.data.summary.failed} appraisals failed to upload`,
        );
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
      event.target.value = ''; // Reset file input
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div
          className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"
          role="status"
          aria-label="Loading"
        ></div>
      </div>
    );
  }

  const columns = [
    {
      key: 'employee',
      header: 'Employee',
      // The one column allowed to wrap, so the table fits its card at laptop
      // widths instead of pushing Actions past the edge.
      cellClassName: 'whitespace-normal',
      render: (appraisal) => (
        <div>
          <div className="text-sm font-medium text-gray-900">
            {appraisal.first_name} {appraisal.last_name}
          </div>
          <div className="text-sm text-gray-500">{appraisal.emp_id}</div>
          <div className="text-xs text-gray-400">{appraisal.department_name}</div>
        </div>
      ),
    },
    {
      key: 'appraisal_date',
      header: 'Appraisal Date',
      render: (appraisal) =>
        appraisal.appraisal_date
          ? formatDateOnly(appraisal.appraisal_date)
          : '-',
    },
    {
      key: 'rating',
      header: 'Rating',
      render: (appraisal) =>
        appraisal.rating ? (
          <div className="flex items-center">
            <span className="text-sm font-medium text-gray-900 mr-2">
              {appraisal.rating}/5
            </span>
            <div className="flex">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`h-4 w-4 ${
                    star <= appraisal.rating
                      ? 'text-warning-500 fill-current'
                      : 'text-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        ) : (
          <span className="text-sm text-gray-500">Not rated</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (appraisal) => (
        <span
          data-testid="appraisals-cell-status"
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClasses(
            appraisal.status,
            'appraisal',
          )}`}
        >
          {formatStatusText(appraisal.status)}
        </span>
      ),
    },
    {
      key: 'next_review',
      header: 'Next Review',
      render: (appraisal) =>
        appraisal.next_appraisal_date
          ? formatDateOnly(appraisal.next_appraisal_date)
          : 'Not set',
    },
    {
      key: 'raise_amount',
      header: 'Raise Amount',
      render: (appraisal) => formatRaise(appraisal.raise_amount, appraisal.currency, 'N/A'),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      cellClassName: 'font-medium',
      render: (appraisal) => (
        <div className="flex items-center justify-end space-x-2">
          <Link
            to={`/appraisals/${appraisal.id}`}
            className="text-primary-600 hover:text-primary-900 p-1"
            title="View Details"
          >
            <Eye className="h-4 w-4" />
          </Link>
          <Link
            to={
              canEditRow(appraisal)
                ? `/appraisals/${appraisal.id}/edit`
                : '#'
            }
            aria-disabled={!canEditRow(appraisal)}
            className={`p-1 ${
              canEditRow(appraisal)
                ? 'text-warning-600 hover:text-warning-900 cursor-pointer'
                : 'text-gray-400 cursor-not-allowed'
            }`}
            title={
              canEditRow(appraisal)
                ? 'Edit'
                : 'You do not have permission to edit appraisals'
            }
            onClick={(e) => {
              if (!canEditRow(appraisal)) {
                e.preventDefault();
              }
            }}
          >
            <Edit className="h-4 w-4" />
          </Link>
          {canEditRow(appraisal) && (
            <button
              onClick={() =>
                toggleAppraisalActive(appraisal.id, appraisal.is_active)
              }
              className={`p-1 rounded transition-colors ${
                appraisal.is_active
                  ? 'text-success-600 hover:text-success-900 hover:bg-success-50'
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
              }`}
              title={appraisal.is_active ? 'Deactivate' : 'Activate'}
            >
              {appraisal.is_active ? (
                <CheckCircle className="h-4 w-4" />
              ) : (
                <XCircle className="h-4 w-4" />
              )}
            </button>
          )}
          <button
            onClick={() => {
              if (hasPermission('appraisals', 'delete')) {
                handleDelete(appraisal.id);
              }
            }}
            className={`p-1 ${
              hasPermission('appraisals', 'delete')
                ? 'text-danger-600 hover:text-danger-900 cursor-pointer'
                : 'text-gray-400 cursor-not-allowed'
            }`}
            title={
              hasPermission('appraisals', 'delete')
                ? 'Delete'
                : 'You do not have permission to delete appraisals'
            }
            disabled={!hasPermission('appraisals', 'delete')}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Appraisals</h1>
          <p className="text-gray-600">
            Manage employee performance appraisals
          </p>
        </div>
        {hasPermission('appraisals', 'create') && (
          <div className="flex space-x-3">
            <button
              onClick={handleDownloadTemplate}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <FileText className="h-4 w-4 mr-2" />
              Download Template
            </button>
            <button
              onClick={() => document.getElementById('fileInput').click()}
              disabled={uploading}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload className="h-4 w-4 mr-2" />
              {uploading ? 'Uploading...' : 'Bulk Upload'}
            </button>
            <AddAppraisalButton />
          </div>
        )}
      </div>

      {/* Summary Cards. Four across only from xl: below that each label has
          under ~110px beside its icon, wraps, and drops that card's number
          below its neighbours'. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
        <SummaryCard
          variant="stat"
          icon={Award}
          iconColor="text-primary-600"
          label="Total Appraisals"
          value={appraisalStats.total}
        />

        <SummaryCard
          variant="stat"
          icon={Star}
          iconColor="text-warning-600"
          label="Average Rating"
          value={
            appraisalStats.avg_rating && !isNaN(appraisalStats.avg_rating)
              ? Number(appraisalStats.avg_rating).toFixed(1)
              : '0.0'
          }
        />

        <SummaryCard
          variant="stat"
          icon={Calendar}
          iconColor="text-success-600"
          label="Due in 30 Days"
          value={upcomingCount ?? '-'}
          data-testid="appraisals-stat-upcoming"
        />

        <SummaryCard
          variant="stat"
          icon={Award}
          iconColor="text-info-600"
          label="Completed"
          value={appraisalStats.completed}
        />
      </div>

      {/* Filters */}
      <CFiltersBar
        searchTestId="appraisals-search"
        clearTestId="appraisals-clear-filters"
        showSearch={true}
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search appraisals..."
        searchLoading={searchLoading}
        gridColsClass="md:grid-cols-5"
        filters={[
          {
            key: 'status',
            testId: 'appraisals-status-filter',
            label: 'Status',
            value: statusFilter,
            onChange: changeFilter(setStatusFilter),
            options: APPRAISAL_STATUSES.map((status) => ({
              value: status.value,
              label: status.label,
            })),
          },
          {
            key: 'year',
            testId: 'appraisals-period-filter',
            label: 'Review Period',
            value: yearFilter,
            onChange: changeFilter(setYearFilter),
            options: [
              { value: '', label: 'All Review Periods' },
              ...getYears().map((year) => ({ value: year, label: year })),
            ],
          },
          {
            key: 'department',
            testId: 'appraisals-department-filter',
            label: 'Department',
            value: departmentFilter,
            onChange: changeFilter(setDepartmentFilter),
            options: [
              { value: '', label: 'All Departments' },
              ...departments.map((dept) => ({
                value: dept.id,
                label: dept.name,
              })),
            ],
          },
        ]}
        onClear={() => {
          setSearchTerm('');
          setDebouncedSearchTerm('');
          setPage(1);
          setStatusFilter('');
          setYearFilter('');
          setDepartmentFilter('');
        }}
      />

      {/* Appraisals List */}
      <CTable
        columns={columns}
        data={appraisals}
        // Spinner instead of rows while a fetch is in flight, so rows from the
        // previous response (e.g. one just deleted) are never actionable.
        loading={searchLoading}
        rowKey="id"
        getRowProps={(appraisal) => ({
          'data-testid': `appraisals-row-${appraisal.id}`,
          'data-testid-group': 'appraisals-row',
        })}
        header={
          <h3 className="text-lg font-medium text-gray-900">
            {total} Appraisal{total !== 1 ? 's' : ''}
          </h3>
        }
        emptyMessage={
          <>
            <Award className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No appraisals found
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || statusFilter || yearFilter || departmentFilter
                ? 'Try adjusting your filters to see more results.'
                : 'Get started by adding your first appraisal record.'}
            </p>
            {hasPermission('appraisals', 'create') && (
              <div className="mt-6">
                <AddAppraisalButton testId="appraisals-empty-add-btn" />
              </div>
            )}
          </>
        }
      />

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />

      {/* Hidden file input */}
      <input
        id="fileInput"
        data-testid="appraisals-upload-input"
        type="file"
        accept=".xlsx,.xls"
        onChange={handleFileUpload}
        style={{ display: 'none' }}
      />

      {/* Upload Results Modal */}
      {showUploadResults && uploadResults && (
        <CModal
          onClose={() => setShowUploadResults(false)}
          labelledBy="upload-results-title"
          // The per-row errors exist nowhere else.
          closeOnBackdrop={false}
          className="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[80vh] overflow-hidden"
        >
          <div className="flex items-center justify-between p-6 border-b">
            <h2 id="upload-results-title" className="text-xl font-bold text-gray-900">
              Upload Results
            </h2>
            <button
              type="button"
              aria-label="Close"
              onClick={() => setShowUploadResults(false)}
              className="text-gray-400 hover:text-gray-700"
            >
              <X className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {/* Summary */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-semibold mb-2">Summary</h3>
              <div className="grid grid-cols-1 gap-1 text-sm sm:grid-cols-3 sm:gap-4">
                <div>
                  <span className="font-medium">Total Processed:</span>{' '}
                  {uploadResults.summary.total}
                </div>
                <div className="text-green-600">
                  <span className="font-medium">Successful:</span>{' '}
                  {uploadResults.summary.success}
                </div>
                <div className="text-red-600">
                  <span className="font-medium">Failed:</span>{' '}
                  {uploadResults.summary.failed}
                </div>
              </div>
            </div>

            {/* Errors */}
            {uploadResults.errors && uploadResults.errors.length > 0 && (
              <div>
                <h3 className="font-semibold mb-3 text-red-600">
                  Errors ({uploadResults.errors.length})
                </h3>
                <div className="space-y-2">
                  {uploadResults.errors.map((error, index) => (
                    <div
                      key={index}
                      className="p-3 bg-red-50 border border-red-200 rounded"
                    >
                      <div className="font-medium text-red-800">
                        Row {error.row}
                      </div>
                      <ul className="mt-1 text-sm text-red-700">
                        {error.messages.map((message, msgIndex) => (
                          <li key={msgIndex}>• {message}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end p-6 border-t">
            <button
              onClick={() => setShowUploadResults(false)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
            >
              Close
            </button>
          </div>
        </CModal>
      )}

      {/* Delete Appraisal Modal */}
      {showDeleteModal && (
        <CModal
          onClose={() => setShowDeleteModal(false)}
          labelledBy="delete-appraisal-title"
          className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative"
        >
          <button
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-2xl font-bold"
            onClick={() => setShowDeleteModal(false)}
            aria-label="Close"
          >
            &times;
          </button>
          <h2 id="delete-appraisal-title" className="text-xl font-bold mb-4 text-danger-700">
            Delete Appraisal
          </h2>
          <p className="mb-4 text-gray-700">
            Are you sure you want to delete this appraisal? This action cannot
            be undone.
          </p>
          <div className="flex justify-end gap-2">
            <button
              className="px-4 py-2 rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 rounded bg-danger-600 text-white hover:bg-danger-700 font-semibold"
              onClick={confirmDelete}
            >
              Delete
            </button>
          </div>
        </CModal>
      )}
    </div>
  );
};

export default Appraisals;
